import { Component, OnInit } from '@angular/core';
import { TypeActiviteService } from '../../../../../../services/typeactivites/typeactivite.service';
import { AppService } from '../../../../../../core/app.service';
import { ActivatedRoute, Router } from '@angular/router';
import { TypeActiviteResponse } from '../../../../../../data/http/responses/typeactivite.response';
import { TypeActiviteRequest } from '../../../../../../data/http/requests/typeactivite.request';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TableTache } from '../../../../../../data/schemas/table.schema';
import { LoaderComponent } from "../../../../../../components/ui/loader/loader.component";
import { UiTable } from "../../../../../../components/ui/table/table.component";
import { ModalComponent } from "../../../../../../components/modal/modal.component";
import { routeAlias } from '../../../../../../../routesalias';
import { Input } from '../../../../../../components/forms/input/input';
import { UiButtonComponent } from '../../../../../../components/button/button.component';
import { RouterLink } from '@angular/router';

@Component({
  selector: 'app-edit',
  imports: [ReactiveFormsModule, LoaderComponent, RouterLink, UiTable, ModalComponent, Input, UiButtonComponent],
  templateUrl: './edit.component.html',
  styleUrl: './edit.component.css'
})
export class EditActivitiesPage implements OnInit {

  public tableTache = TableTache
  typeActiviteForm!: FormGroup;
  taskForm!: FormGroup;
  editTaskForm!: FormGroup;
  typeActivityId: string | null = null;
  typeActivite: TypeActiviteResponse | null = null;
  public isLoading: boolean = false;
  currentTypeActiviteData: any | null = null;
  public isModalVisible: boolean = false; // Contrôle la visibilité du modal
  public editingTask: any | null = null;
  public routeAlias = routeAlias;

  constructor(
    private typeActiviteService: TypeActiviteService,
    private appService: AppService,
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder
  ) { }

  ngOnInit(): void {

    this.initForms();

    // Récupérez l'ID du paramètre de l'URL
    this.typeActivityId = this.route.snapshot.paramMap.get('id');

    if (this.typeActivityId) {
      this.loadTypeActivityById(this.typeActivityId);
    } else {
      console.error('Aucun ID d\'activité trouvé dans l\'URL.');
      this.loadDataFromLocalStorage();
    }
  }

  // Méthode pour initialiser les formulaires
  initForms(): void {
    this.typeActiviteForm = this.fb.group({
      nomTypeActivite: ['', Validators.required]
    });

    this.taskForm = this.fb.group({
      tache: ['', Validators.required],
      poids: ['', [Validators.required, Validators.pattern(/^\d+$/)]] // Le poids doit être un nombre entier
    });

    // Initialisation du formulaire d'édition de tâche
    this.editTaskForm = new FormGroup({
      tache: new FormControl('', Validators.required),
      poids: new FormControl('', [Validators.required, Validators.pattern('^\\d+$')])
    });
  }

  // Méthode pour charger les données depuis le localStorage et mettre à jour l'état du composant et les formulaires
  loadDataFromLocalStorage(): void {
    const storedData = localStorage.getItem('currentTypeActiviteForEdit');
    if (storedData) {
      try {
        const parsedData: TypeActiviteRequest = JSON.parse(storedData);
        this.currentTypeActiviteData = parsedData;

        // Patch le formulaire principal avec les données chargées
        this.typeActiviteForm.patchValue({
          nomTypeActivite: parsedData.type_activite
        });

        // Assurez-vous que les tâches ont un _index pour l'affichage si nécessaire par la table
        if (this.currentTypeActiviteData.taches) {
          this.currentTypeActiviteData.taches = this.currentTypeActiviteData.taches.map((task: any, i: any) => ({
            ...task,
            _index: i + 1 // Assurez-vous que _index commence à 1 pour l'affichage
          }));
        }
        //console.log('Données chargées depuis le localStorage et formulaires patchés :', this.currentTypeActiviteData);
      } catch (e) {
        console.error('Erreur lors de la lecture ou du parsing de "currentTypeActiviteForEdit" depuis le Storage:', e);
      }
    }
  }

  // Méthode pour ouvrir le modal et pré-remplir le formulaire d'édition
  onEditTask(task: any): void {
    this.editingTask = { ...task }; // Crée une copie pour éviter la modification directe
    // Extraire le nom de la tâche sans le préfixe "Tn : " pour l'édition
    const taskNameWithoutPrefix = task.tache.replace(/^T\d+\s:\s/, '');
    this.editTaskForm.patchValue({
      tache: taskNameWithoutPrefix,
      poids: task.poids
    });
    this.isModalVisible = true;
  }

  // Méthode appelée lors de la soumission du formulaire
  onSubmitTypeActivite(): void {
    if (this.typeActiviteForm.valid) {
      const formData = this.typeActiviteForm.value; // Récupérez les données du formulaire réactif
      //console.log('Formulaire de type d\'activité soumis :', formData);

      const existingTaches = this.currentTypeActiviteData?.taches || [];
      const typeActiviteToStore: TypeActiviteRequest = {
        applicationId: this.appService.getAppId(),
        type_activite: formData.nomTypeActivite,
        taches: existingTaches
      };
      this.currentTypeActiviteData = typeActiviteToStore

      if (this.currentTypeActiviteData.taches) {
        this.currentTypeActiviteData.taches = this.currentTypeActiviteData.taches.map((task: any, i: any) => ({
          ...task,
          _index: i + 1
        }));
      }

      localStorage.setItem('currentTypeActiviteForEdit', JSON.stringify(typeActiviteToStore));
      //this.isEditingTypeActivite = false
      this.typeActiviteForm.get('nomTypeActivite')?.disable();
    } else {
      console.warn('Le formulaire est invalide. Veuillez remplir tous les champs requis.');
      // Optionnel : marquez tous les champs comme "touchés" pour afficher les erreurs de validation
      this.typeActiviteForm.markAllAsTouched();
    }
  }

  // Méthode pour sauvegarder les modifications de la tâche
  onSaveEditedTask(): void {
    if (this.editTaskForm.valid && this.editingTask && this.currentTypeActiviteData?.taches) {
      const updatedValues = this.editTaskForm.value;

      // Retrouver l'index de la tâche originale dans le tableau
      const taskIndex = this.currentTypeActiviteData.taches.findIndex((t: any) => t._index === this.editingTask._index);

      if (taskIndex !== -1) {
        // Mettre à jour la tâche avec les nouvelles valeurs
        // Reconstruire le nom de la tâche avec le préfixe "Tn : "
        this.currentTypeActiviteData.taches[taskIndex].tache = `T${this.editingTask._index} : ${updatedValues.tache}`;
        this.currentTypeActiviteData.taches[taskIndex].poids = updatedValues.poids;

        localStorage.setItem('currentTypeActiviteForEdit', JSON.stringify(this.currentTypeActiviteData));
        this.onCloseModal(); // Fermer le modal après la sauvegarde
      } else {
        console.error("Tâche à modifier non trouvée.");
      }
    } else {
      console.warn('Le formulaire d\'édition de tâche est invalide.');
      this.editTaskForm.markAllAsTouched();
    }
  }

  // Méthode pour supprimer une tâche
  onDeleteTask(taskToDelete: any): void {
    if (this.currentTypeActiviteData && this.currentTypeActiviteData.taches) {
      // Filtrer la tâche à supprimer
      this.currentTypeActiviteData.taches = this.currentTypeActiviteData.taches.filter(
        (task: any) => task._index !== taskToDelete._index
      );

      // Re-indexer les tâches après suppression
      this.currentTypeActiviteData.taches = this.currentTypeActiviteData.taches.map((task: any, i: any) => ({
        ...task,
        _index: i + 1
      }));

      localStorage.setItem('currentTypeActiviteForEdit', JSON.stringify(this.currentTypeActiviteData));
    }
  }

  // Méthode pour fermer le modal
  onCloseModal(): void {
    this.isModalVisible = false;
    this.editingTask = null;
    this.editTaskForm.reset(); // Réinitialiser le formulaire d'édition
  }

  loadTypeActivityById(id: string): void {
    this.typeActiviteService.getById(id).subscribe({
      next: (response: any) => {
        this.typeActivite = response.data;


        if (this.typeActivite) {
          const typeActiviteForLocalStorage: TypeActiviteRequest = {
            // L'applicationId est souvent un contexte, récupéré des cookies ou de la session
            applicationId: this.appService.getAppId() || '',
            type_activite: this.typeActivite.type_activite,
            taches: this.typeActivite.taches?.map((tache, index) => ({
              tache: tache.tache,
              poids: tache.poids,
              _index: index
            }))
          };

          // Stocker l'objet transformé dans le localStorage
          localStorage.setItem('currentTypeActiviteForEdit', JSON.stringify(typeActiviteForLocalStorage));
          this.loadDataFromLocalStorage()
        }
      },
      error: (error) => {
        console.error('Erreur lors du chargement de l\'activité par ID :', error);
      }
    })
  }

  onAddTask(): void {
    if (this.taskForm.valid && this.currentTypeActiviteData) {
      const rawNewTask = this.taskForm.value; // Récupère les valeurs brutes du formulaire

      if (!this.currentTypeActiviteData.taches) {
        this.currentTypeActiviteData.taches = [];
      }

      // Calcule l'index pour la nouvelle tâche (basé sur la longueur actuelle du tableau)
      const newIndex = this.currentTypeActiviteData.taches.length + 1;

      // Crée l'objet tâche avec la propriété 'tache' formatée et '_index'
      const formattedTask = {
        tache: `T${newIndex} : ${rawNewTask.tache}`, // Ajoute le préfixe "Tn : "
        poids: rawNewTask.poids,
        _index: newIndex // Assurez-vous que _index est également défini
      };

      this.currentTypeActiviteData.taches.push(formattedTask);
      this.currentTypeActiviteData.taches = this.currentTypeActiviteData.taches.map((task: any, i: any) => ({
        ...task,
        _index: i + 1
      }));

      localStorage.setItem('currentTypeActiviteForEdit', JSON.stringify(this.currentTypeActiviteData));
      this.taskForm.reset();
      this.taskForm.get('tache')?.setValue('');
      this.taskForm.get('poids')?.setValue('');
      this.taskForm.markAsUntouched();
      this.taskForm.markAsPristine();
    } else {
      console.warn('Le formulaire de tâche est invalide. Veuillez remplir tous les champs requis.');
      this.taskForm.markAllAsTouched();
    }
  }

  onSubmit(): void {
    if (this.currentTypeActiviteData != null) {
      this.isLoading = true;
      this.typeActiviteService.update(this.typeActivityId!, this.currentTypeActiviteData).subscribe({
        next: (createdStructure) => {
          this.router.navigate([
            routeAlias.parameters.home,
            routeAlias.parameters.activity.home,
            routeAlias.parameters.activity.type_activite.home
          ]);
          this.isLoading = false
        },
        error: (error) => {
          console.error('Erreur lors de la création de la structure programmatique :', error);
          this.isLoading = false
        }
      });
    } else {
      console.warn('Le nom de la structure programmatique ne peut pas être vide.');
    }
  }
}
